import React, { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { useNavigate, useParams } from 'react-router-dom'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import Page from './Components/Page'
import { processDataService } from '../../services/processDataService'
import { useAuthContext } from '../../hooks/useAuthContext'
import { activityTemplateService } from '../../services/activityTemplateService'
import { pageTemplateService } from '../../services/pageTemplateService'
import {
  useAllValues,
  useCommentValues,
  useFieldCurrencyMap,
  useFieldValidationError,
  usePageDetails,
  usePreviousProcessData,
  useProcessContext,
  useReviewValues,
  useSelectedTableData,
  useTableSequenceAndHeaders,
} from '../../store'
import {
  ACTIVITY_TYPES,
  COMPUTED_OPERATION_TYPES,
  CURRENCY_COUNTRY_CODE,
  FIELD_TYPES,
  NUMBER_OF_TASKS_AND_QUERIES_ANSWERED_AT_A_TIME_BY_USER,
  REDIRECT_TIMER,
  REVIEW_CONTEXTS,
  ROUTE_TYPES,
  STYLES,
  TOAST_SETTINGS,
  TOAST_SETTINGS_ERROR,
} from '../../utility/constants'
import makeStyles from '@mui/styles/makeStyles'
import { processTemplateService } from '../../services/processTemplateService'
import { fieldTemplateService } from '../../services/fieldTemplateService'
import { dependencyMapService } from '../../services/dependencyMapService'
import { listDataService } from '../../services/listDataService'
import DataEntryFormFooter from '../../components/DataEntry/DataEntryFormFooter'
import { toast } from 'react-toastify'
import { finalConstraintsValidation, requiredFieldValidation } from './utils/fieldUtil'
import ConfirmationBox from '../../components/Dashboard/ConfirmationBox'
import { queryService } from '../../services/queryService'
import DataEntryPageNav from './Components/DataEntryPageNav'
import { CircularProgress, Dialog, IconButton, Typography, Box, Collapse } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { getFieldIdFromKey, getPageId } from './utils/utils'
import { userConfig } from '../../services/userConfigServices'
import { executeOutboundServerAction } from './utils/outboundAction'
import Loader from '../../components/core-ui/Loader'
import { currencyFieldService } from '../../services/currencyFieldService'
import { computeCurrency } from './utils/currency'
import DataPreview from '../../components/DataPreview/DataPreview'
import { DocumentService } from '../../services/documentsServices'
import { useResponsiveTypography } from '../../utility/responsiveTypography'
import useIdleRedirect from '../../hooks/useIdleRedirect'
import { useDeadlineCountdown } from '../../hooks/useDeadlineCountdown'
import { formatDeadlineCountdown } from '../../utility/deadlineUtils'
import { indexedDbStorage } from '../../utility/indexedDbStorage'
import { adminHolidayService } from '../../services/admin/holidays'
import { keyframes } from '@mui/material'
import { useMyTasks } from '../../context/MyTasksContext'
import { getTimeInMillis } from '../../utility/datetime'
import { convertFromLegacyToRowWiseData } from './utils/tableFieldUtil'
import {
  autoComputeFieldValueOptimized,
  computeTableValuesOnload,
} from './utils/autoComputeOptimized'
import { useBadges } from '../../context/BadgeContext'

const fadeSlideIn = keyframes`
  0%   { transform: translateY(-10px) scale(0.95); opacity: 0; }
  100% { transform: translateY(0)     scale(1);    opacity: 1; }
`

const useStyles = makeStyles(STYLES.COMMON)
const confirmationText =
  'All fields have been accepted, no further modifications by the assignee are allowed.'
const ONLOAD_COMPUTED_OPERATION_TYPES = new Set([
  COMPUTED_OPERATION_TYPES.CONCAT,
  COMPUTED_OPERATION_TYPES.MULTIPLY,
  COMPUTED_OPERATION_TYPES.SUM,
  COMPUTED_OPERATION_TYPES.CONCAT_TABLE_COLUMN,
  COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN,
  COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN,
  COMPUTED_OPERATION_TYPES.DATE_COMPUTATION,
])
const DataEntryPage = ({
  processId: propProcessId,
  activityTemplateId: propActivityTemplateId,
  isModal = false,
  onSuccess,
  onClose,
} = {}) => {
  useIdleRedirect('/tasks', isModal) // disabled when embedded in a modal
  const { dataEntryPageTitle, dataEntrysubtitle } = useResponsiveTypography()

  const classes = useStyles()
  const navigate = useNavigate()

  const { user } = useAuthContext()
  const { process_id: routeProcessId, activity_template_id: routeActivityTemplateId } = useParams()

  // Props take priority over URL params — allows use as a modal component
  const processId = propProcessId || routeProcessId
  const activityTemplateId = propActivityTemplateId || routeActivityTemplateId

  const [processData, setProcessData] = useState(null)
  const [processTemplate, setProcessTemplate] = useState(null)
  const [currentPageIndex, setCurrentPageIndex] = useState(null)
  const [pages, setPages] = useState([])
  const [activityTemplate, setActivityTemplate] = useState(null)
  const [isSubmissionDialogOpen, setSubmissionDialogOpen] = useState(false)
  const [allToRoutes, setAllToRoutes] = useState([])
  const [selectedToRoute, setSelectedToRoute] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [tableConstraints, setTableConstraints] = useState([])
  const [fieldConstraints, setFieldConstraints] = useState([])
  const [allSubfields, setAllSubfields] = useState([])
  const [listDataMetadata, setListDataMetadata] = useState([])
  const [showGoBackWarningDialog, setShowGoBackWarningDialog] = useState(false)
  const [fieldConstraintHiddenRoutes, setFieldConstraintHiddenRoutes] = useState([])
  const [contentLoaded, setContentLoaded] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [closeCount, setCloseCount] = useState(0)
  const [isReopening, setIsReopening] = useState(false)
  const [expandedNotifications, setExpandedNotifications] = useState({})
  const { markStale, deleteTaskLocally } = useMyTasks()
  const { decrementTaskBadge } = useBadges()

  const VISIBLE_LI_COUNT = 3

  const notificationsRef = useRef([])
  const computedProcessRef = useRef(new Set())
  const computedValuesRef = useRef({})

  // Info toast on load + expiry toast & redirect
  const autoCloseToastShown = useRef(false)
  const expiryHandled = useRef(false)

  const setPageDetails = usePageDetails((state) => state.setPageDetails)

  const allValues = useAllValues((state) => state.allValues)
  const setInitialAllValues = useAllValues((state) => state.setInitialAllValues)
  const reviewValues = useReviewValues((state) => state.reviewValues)
  const setReviewValues = useReviewValues((state) => state.setReviewValues)
  const commentValues = useCommentValues((state) => state.commentValues)
  const setCommentValues = useCommentValues((state) => state.setCommentValues)
  const setSelectedData = useSelectedTableData((state) => state.setSelectedData)
  const selectableTableData = useSelectedTableData((state) => state.selectedTableData)
  const setPreviousProcessDataValues = usePreviousProcessData(
    (state) => state.setPreviousProcessData,
  )

  const setInitialFieldCurrencyMap = useFieldCurrencyMap(
    (state) => state.setInitialFieldCurrencyMap,
  )
  const fieldCurrencyMap = useFieldCurrencyMap((state) => state.fieldCurrencyMap)

  /* process context */
  const processContext = useProcessContext((state) => state.processContext)
  const setProcessContext = useProcessContext((state) => state.setProcessContext)

  const notificationsProcessIdRef = useRef(null)

  /* Table Sequence and Headers */
  const tableSequenceAndHeaders = useTableSequenceAndHeaders(
    (state) => state.tableSequenceAndHeaders,
  )
  const setTableSequenceAndHeaders = useTableSequenceAndHeaders(
    (state) => state.setTableSequenceAndHeaders,
  )

  /* disabled field to skip validation */
  const setFieldValidationError = useFieldValidationError((state) => state.setFieldValidationError)

  const [isPageReady, setIsPageReady] = useState(false)
  const { headingStyles, paragraphStyles } = useResponsiveTypography()

  // Compute the deadline Date from metadata (or fallback from created_at + time_in_min)
  const deadlineDate = React.useMemo(() => {
    if (!activityTemplate?.time_in_min || !['active', 'open'].includes(processData?.status)) {
      return null
    }

    if (processData?.metadata?.auto_close_deadline) {
      const m = moment.utc(processData.metadata.auto_close_deadline).local()
      return m.isValid() ? m.toDate() : null
    }

    if (processData?.created_at) {
      const m = moment(processData.created_at)
      if (m.isValid()) return m.clone().add(activityTemplate.time_in_min, 'minutes').toDate()
    }

    return null
  }, [activityTemplate, processData])

  // Live countdown from the shared hook
  const deadlineCountdown = useDeadlineCountdown(deadlineDate)
  const isDeadlineExpired = deadlineCountdown?.isExpired ?? false

  // add useEffect and handlers below
  useEffect(() => {
    computedProcessRef.current.clear()
    computedValuesRef.current = {}
  }, [processId, activityTemplateId])

  // useEffect(() => {
  //   console.log('allValues---', allValues)
  // }, [allValues])

  useEffect(() => {
    if (!deadlineDate) return

    // Show info toast once when deadline is still in the future
    if (!isDeadlineExpired && !autoCloseToastShown.current) {
      autoCloseToastShown.current = true
      const formatted = formatDeadlineCountdown(deadlineDate)
      if (formatted && !formatted.isExpired) {
        toast.info(`${formatted.text}`, {
          position: 'top-right',
          autoClose: 10000,
        })
      }
    }

    // Handle expiry: warning toast + redirect (once)
    if (isDeadlineExpired && !expiryHandled.current) {
      expiryHandled.current = true
      toast.warn("This task's auto-close deadline has expired. Redirecting to tasks...", {
        toastId: 'auto-close-expired',
        autoClose: 5000,
      })
      setTimeout(() => {
        if (isModal && onClose) {
          onClose()
        } else {
          navigate('/tasks')
        }
      }, 5000)
    }
  }, [deadlineDate, deadlineCountdown, isDeadlineExpired, navigate])

  /*<---------Bubble notifications---------->*/
  const customViewValuesStr = useMemo(() => {
    const values =
      processContext?.allFields
        ?.filter((f) => f.data_structure === FIELD_TYPES.CUSTOM_VIEW)
        ?.map((f) => allValues?.[`f_${f.id}`]) ?? []
    return JSON.stringify(values)
  }, [processContext?.allFields, allValues])

  useEffect(() => {
    if (!processContext?.processData?.id) return
    if (String(processContext.processData.id) !== String(processId)) return

    const customViewFields =
      processContext?.allFields?.filter((f) => f.data_structure === FIELD_TYPES.CUSTOM_VIEW) || []

    if (!customViewFields.length) return

    const customViewDataMap = customViewFields.reduce((acc, field) => {
      const tableData = allValues?.[`f_${field.id}`]
      if (Array.isArray(tableData) && tableData.length) {
        acc[field.id] = tableData
      }
      return acc
    }, {})

    if (!Object.keys(customViewDataMap).length) return

    const checkChangeInPublishedFile = async () => {
      const { process_reference_num, status, process_template_id } = processContext.processData

      const promises = Object.entries(customViewDataMap).map(([fieldId, tableData]) =>
        processDataService
          .bubbleNotification(user, {
            records: tableData,
            current_process_template_id: process_template_id,
            status: status,
            target: 'dms process',
            current_process_reference_num: process_reference_num,
            currentFieldId: Number(fieldId),
          })
          .catch((err) => {
            console.error(`bubbleNotification failed for field ${fieldId}:`, err)
            return null
          }),
      )

      const results = await Promise.all(promises)

      if (String(processContext.processData.id) !== String(processId)) return

      const allNotifications = results.flatMap((res) => {
        const notificationsData = res?.data?.notification || []
        return notificationsData.map((n, i) => ({
          ...n,
          open: true,
          id: Date.now() + i,
        }))
      })

      if (allNotifications.length) {
        notificationsRef.current = allNotifications
        notificationsProcessIdRef.current = processId
        setNotifications(allNotifications)
      }
    }

    checkChangeInPublishedFile()
  }, [customViewValuesStr, processContext?.processData?.id])

  useEffect(() => {
    const closed = notifications.filter((n) => !n.open)
    if (!closed.length) return

    const timer = setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.open))
    }, 300)

    return () => clearTimeout(timer)
  }, [notifications])

  const handleReopenNotifications = () => {
    if (notificationsProcessIdRef.current !== processId) return
    const restored = notificationsRef.current.map((n) => ({ ...n, open: true }))
    setIsReopening(true)
    setNotifications(restored)
    setTimeout(() => setIsReopening(false), 400) // match animation duration
  }

  // Reset both refs together

  useEffect(() => {
    notificationsRef.current = []
    notificationsProcessIdRef.current = null
    setNotifications([])
    setCloseCount(0) //  reset
  }, [processId])

  const handleCloseNotification = (id) => {
    setCloseCount((prev) => prev + 1) //  increment on each close
    setTimeout(() => {
      setNotifications((prev) =>
        prev.map((item) => (item.id === id ? { ...item, open: false } : item)),
      )
    }, 400)
  }
  /*<---------XXXXXXXXXXXX---------->*/

  const getAllActivityData = async (user, activityTemplateId) => {
    try {
      const allPromises = [
        dependencyMapService.getListDataDep(user, activityTemplateId),
        activityTemplateService.getTemplate(user, activityTemplateId),
        fieldTemplateService.getConditionalEnableDisable(user, activityTemplateId),
        fieldTemplateService.getFieldConstraints(user, activityTemplateId),
        activityTemplateService.getAllToRoutes(user, activityTemplateId),
        fieldTemplateService.getTableConstraints(user, activityTemplateId),
        fieldTemplateService.getHighLightConstraints(user, activityTemplateId),
        fieldTemplateService.getFieldConstraintHiddenRoute(user),
      ]
      const activityRelatedData = await Promise.all(allPromises)
      const allData = {
        listDataDep: activityRelatedData[0].data,
        activityTemplate: activityRelatedData[1].data,
        conditionalEnableDisable: activityRelatedData[2].data,
        fieldConstraints: activityRelatedData[3].data,
        allToRoutes: activityRelatedData[4].data,
        tableConstraints: activityRelatedData[5].data,
        fieldHighlights: activityRelatedData[6].data,
        fieldConstraintHiddenRoutes: activityRelatedData[7].data,
      }

      return allData
    } catch (error) {
      console.error('getAllActivityData', error.message)
    }
  }

  // In DataEntryPage — watch allValues and recompute DATE_COMPUTATION fields reactively
  useEffect(() => {
    if (!isPageReady || !processContext?.allFields?.length || !contentLoaded) return

    const dateComputedFields = processContext.allFields.filter(
      (f) => f.is_computed && f.metadata?.operation === COMPUTED_OPERATION_TYPES.DATE_COMPUTATION,
    )

    if (!dateComputedFields.length) return

    // Collect all field_template_ids that DATE_COMPUTATION fields depend on
    const watchedFieldIds = new Set()
    dateComputedFields.forEach((f) => {
      const dc = f.metadata?.date_config
      if (dc?.source_date?.field_template_id) {
        watchedFieldIds.add(`f_${dc.source_date.field_template_id}`)
      }
      if (dc?.duration?.list_field_template_id) {
        watchedFieldIds.add(`f_${dc.duration.list_field_template_id}`)
      }
      if (dc?.duration?.numeric_field_template_id) {
        watchedFieldIds.add(`f_${dc.duration.numeric_field_template_id}`)
      }
    })
    ;(async () => {
      try {
        const ctx = {
          processPrefix: processContext?.processTemplate?.prefix,
          processReferenceNum: processContext?.processData?.process_reference_num,
          processTemplateId: processContext?.processData?.process_template_id,
          version: processContext?.processData?.version,
        }

        const idFieldMap = {}
        processContext.allFields?.forEach((cur) => {
          idFieldMap[cur.id] = cur
        })

        const computedValues = await autoComputeFieldValueOptimized({
          allActivitiesCreatedAtWithVersion: processContext?.allActivitiesCreatedAtWithVersion,
          allProcessData: processContext?.allProcessData,
          context: ctx,
          dynamicListData: null,
          holidayList: processContext?.holidayList,
          listDataIdMetadataMap: processContext.listDataIdMetadataMap,
          selectedTableData: null,
          tableRowIndex: null,
          tableSubfieldsMap: null,
          user: user,
          computeFields: dateComputedFields, // processContext.allFields.filter((it) => it.is_computed && !it.is_subfield),
          data: { allValues, rowValues: {} },
          idFieldMap,
        })

        // Only update store if computed values actually changed
        const hasChanges = Object.entries(computedValues).some(
          ([key, val]) => allValues[key] !== val,
        )

        if (hasChanges) {
          setInitialAllValues({ ...allValues, ...computedValues })
        }
      } catch (error) {
        console.error('DATE_COMPUTATION reactive recompute failed:', error)
      }
    })()
  }, [allValues, isPageReady, contentLoaded])

  useEffect(() => {
    return () => {
      setFieldValidationError({})
    }
  }, [])

  useEffect(() => {
    if (allToRoutes != null) {
      setSelectedToRoute(allToRoutes[0]?.id)
    }
  }, [allToRoutes])

  // get all process data
  useEffect(() => {
    if (!user?.token) {
      return
    }

    // eslint-disable-next-line no-extra-semi
    ;(async () => {
      try {
        const allActivityData = await getAllActivityData(user, activityTemplateId)
        setAllToRoutes(allActivityData.allToRoutes)
        setActivityTemplate(allActivityData.activityTemplate)
        setTableConstraints(allActivityData.tableConstraints)
        setFieldConstraints(allActivityData.fieldConstraints)
        setFieldConstraintHiddenRoutes(allActivityData.fieldConstraintHiddenRoutes)

        /* get process data */
        const processData = await processDataService.getData(user, processId)
        setProcessData(processData.data[0])

        /* get process template */
        const processTemplate = await processTemplateService.getProcessTemplate(
          user,
          processData.data[0]?.process_template_id,
        )
        setProcessTemplate(processTemplate.data[0])

        /* get all pages for activity */
        const dataEntryActivityId =
          allActivityData.activityTemplate?.activity_type == ACTIVITY_TYPES.REVIEW
            ? allActivityData.activityTemplate?.associated_data_entry_activity_template_id
            : activityTemplateId

        const tableSubfieldsMap = await fieldTemplateService.getTableSubfieldsMapInActivity(
          user,
          dataEntryActivityId,
        )

        const pageTemplates = await pageTemplateService.getPagesForActivity(
          user,
          dataEntryActivityId,
        )
        setPages(pageTemplates.data)

        const allSubfieldsResult = await fieldTemplateService.getAllFieldsInActivity(
          user,
          dataEntryActivityId,
        )
        setAllSubfields(allSubfieldsResult.data)

        const tableSubfields = await fieldTemplateService.getTableSubfields(
          user,
          dataEntryActivityId,
        )

        const listDataMetadata =
          (await listDataService.getListDataMetadata(user, dataEntryActivityId))?.data || []
        setListDataMetadata(listDataMetadata)

        let localPrefetchResult = { externalValues: {}, fieldMappings: {}, fieldMetadata: {} }
        const hasPlaceholders = listDataMetadata.some(
          (item) =>
            item.placeholders && Array.isArray(item.placeholders) && item.placeholders.length > 0,
        )
        if (hasPlaceholders) {
          localPrefetchResult = await listDataService.getPlaceholderPrefetch(
            user,
            activityTemplateId,
            processId,
            processData.data[0]?.process_reference_num,
            processData.data[0]?.process_template_id,
          )
        }

        /* get field_dep - TODO: cache */
        const fieldTemplateDep = (
          await dependencyMapService.getFieldTemplateDep(user, dataEntryActivityId)
        ).data

        const previousProcessData = await processDataService.getPreviousProcessData(user, processId)
        setPreviousProcessDataValues(previousProcessData.data)

        // Updates cached list data of all lists in current activity
        const listDataForActivity = await listDataService.getDataActivity(user, activityTemplateId)
        for (const [key, value] of Object.entries(listDataForActivity)) {
          await indexedDbStorage.setItem(key, value)
        }

        const { computedSumAcrossProcess, ndc } = (
          await processDataService.getSumAcrossProcessComputation(user, processData.data[0]?.id)
        ).data

        const processAllRoutes = await activityTemplateService.getProcessAllRoutes(
          user,
          processData.data[0]?.process_template_id,
        )

        if (!currentPageIndex) {
          const processId = processData?.data[0]?.id
          if (processId) {
            const defaultNavigatePage = await pageTemplateService.getDefaultPage(user, processId)
            currentPageIndex == null &&
              setCurrentPageIndex(() => defaultNavigatePage?.default_page_sequence - 1 || 0)
          }
        }

        const userTeamMap = await userConfig.getUserTeamMap(user)

        const nonDefaultCurrency = await currencyFieldService.getNonDefaultCurrency(processId, user)

        // set initial fieldCurrencyMap
        const fieldCurrencyMapData = nonDefaultCurrency?.reduce((acc, cur) => {
          return { ...acc, [cur.field_template_id]: CURRENCY_COUNTRY_CODE[cur.currency_code] }
        }, {})

        const companyDetails = await currencyFieldService.getCompanyData(user)

        const conditionalEnableDisable = (
          await fieldTemplateService.getConditionalEnableDisable(user, dataEntryActivityId)
        ).data

        /* get field hidden fields - TODO: cache */
        const hiddenFields = (
          await fieldTemplateService.getHiddenFieldsForUser(user, dataEntryActivityId)
        ).data

        /* get tables sequence and headers*/
        const tableSequenceAndHeaders = await fieldTemplateService.getTableSequenceAndHeaders(
          user,
          processData.data[0]?.id,
        )

        setTableSequenceAndHeaders(tableSequenceAndHeaders)

        const documents = await DocumentService.checkForTemplateDoc(user, activityTemplateId)
        const dynamicListData = await userConfig.getDynamicList(user)

        const listDataDep = (await dependencyMapService.getListDataDep(user, dataEntryActivityId))
          .data

        const holidayList = await adminHolidayService.getHolidays(user)

        const allActivitiesCreatedAtWithVersion = await processDataService.getActivityVersionInfo(
          user,
          processData.data[0]?.process_template_id,
          processData.data[0]?.process_reference_num,
        )

        const listDataIdMetadataMap = listDataMetadata.reduce((acc, cur) => {
          acc[cur.list_data_id] = cur
          return acc
        }, {})

        // Compact Set of list_ids whose options contain [[placeholder]] patterns.
        // Replaces listIdToEntriesMap — no need to store entire entry arrays just to detect
        // whether a list has placeholders. O(1) Set.has() replaces O(k) entries scan.
        const listIdsWithPlaceholders = new Set()
        const seen = new Set()
        for (const item of listDataMetadata) {
          if (item.list_id == null || seen.has(item.list_id)) continue
          seen.add(item.list_id)
          const hasPlaceholder = Array.isArray(item.placeholders) && item.placeholders.length > 0
          if (hasPlaceholder) listIdsWithPlaceholders.add(item.list_id)
        }

        /* set all process data to context */

        const nextProcessContext = {
          processData: processData.data[0],
          processTemplate: processTemplate?.data[0],
          activityTemplate: allActivityData.activityTemplate,
          fieldConstraints: allActivityData.fieldConstraints,
          conditionalEnableDisable: conditionalEnableDisable,
          listDataMetadata: listDataMetadata,
          listDataIdMetadataMap: listDataIdMetadataMap,
          listIdsWithPlaceholders: listIdsWithPlaceholders,
          fieldTemplateDep: fieldTemplateDep,
          listDataDep: listDataDep,
          userTeamMap: userTeamMap.data,
          hiddenFields: hiddenFields,
          allFields: allSubfieldsResult.data,
          tableSubfieldsMap: tableSubfieldsMap.data,
          tableSubfields: tableSubfields.data,
          processAllRoutes: processAllRoutes.data,
          companyDetails: companyDetails,
          nonDefaultCurrency: nonDefaultCurrency,
          hasDocuments: documents && documents.length > 0,
          dynamicListData: dynamicListData.data,
          holidayList: holidayList,
          allActivitiesCreatedAtWithVersion: allActivitiesCreatedAtWithVersion,
          externalValues: localPrefetchResult?.externalValues || {},
          fieldMappings: localPrefetchResult?.fieldMappings || {},
          fieldMetadata: localPrefetchResult?.fieldMetadata || {},
        }
        setProcessContext(nextProcessContext)

        const pageWiseFields = allSubfieldsResult.data?.reduce((acc, cur) => {
          if (acc[getPageId(cur.page_template_id)]) {
            return {
              ...acc,
              [getPageId(cur.page_template_id)]: [...acc[getPageId(cur.page_template_id)], cur],
            }
          } else {
            return { ...acc, [getPageId(cur.page_template_id)]: [cur] }
          }
        }, {})
        setPageDetails({ ...pageWiseFields })

        const initialAllValues = processData?.data[0]?.data || {}

        if (allActivityData?.activityTemplate?.activity_type != ACTIVITY_TYPES.REVIEW) {
          const { updatedFieldCurrencyMap, nonDefaultValues } = computeCurrency(
            fieldCurrencyMapData,
            allSubfieldsResult.data,
            companyDetails?.default_currency_code,
            allValues,
          )

          let updatedCurrencyMap = {
            ...updatedFieldCurrencyMap,
            ...nonDefaultValues.reduce((acc, cur) => {
              return { ...acc, [cur.id]: cur.currency }
            }, {}),
          }

          // need to update the sum across process field currency to db
          for (let [key, value] of Object.entries(ndc)) {
            if (value !== companyDetails?.default_currency_code) {
              await currencyFieldService.addNonDefaultCurrency(user, {
                field_template_id: key,
                currency_code: value,
                process_id: processData.data[0]?.id,
              })
            }
          }

          setInitialFieldCurrencyMap({
            ...fieldCurrencyMapData,
            ...updatedCurrencyMap,
            ...Object.entries(ndc).reduce((acc, [key, value]) => {
              return { ...acc, [key]: CURRENCY_COUNTRY_CODE[value] }
            }, {}),
          })
        } else {
          setInitialFieldCurrencyMap(fieldCurrencyMapData)
        }

        //----- onload table computation -----//

        let calculatedTableRelatedValues = computedValuesRef.current

        const processKey = `${processId}_${activityTemplateId}`
        const now = getTimeInMillis()

        const valuesForComputation = {
          ...initialAllValues,
          ...computedSumAcrossProcess,
        }

        const isReadyForOnloadComputation =
          tableSubfields?.data?.length > 0 &&
          tableSequenceAndHeaders?.length > 0 &&
          Object.keys(valuesForComputation).length > 0 &&
          Object.keys(listDataIdMetadataMap || {}).length > 0

        // console.log('Onload Computation Check', {
        //   processKey,
        //   alreadyComputed: computedProcessRef.current.has(processKey),
        //   tableSubfields: tableSubfields?.data?.length,
        //   tableHeaders: tableSequenceAndHeaders?.length,
        //   valuesCount: Object.keys(valuesForComputation).length,
        // })

        if (!computedProcessRef.current.has(processKey) && isReadyForOnloadComputation) {
          try {
            console.time('Table Row Wise Computation')

            for (const tableField of tableSubfields.data) {
              const columns = tableField?.subfields

              const allRows = tableSequenceAndHeaders
                ?.find((it) => it.table_field_template_id == tableField.id)
                ?.sequence_header_map?.map((it) => it.row)

              const tableDataConverted = convertFromLegacyToRowWiseData(
                tableField.id,
                valuesForComputation,
                columns,
              )

              const computedValues = await computeTableValuesOnload({
                allRows,
                columns,
                allValues: {
                  ...valuesForComputation,
                  ...tableDataConverted,
                },
                tableFieldId: tableField.id,
                listDataIdMetadataMap,
                processContext: nextProcessContext,
                updatedAt: now,
                user,
                selectedTableData: processData?.data[0]?.selectable_tables_data,
              })

              calculatedTableRelatedValues = {
                ...calculatedTableRelatedValues,
                ...computedValues.values,
                updatedAt: now + 1,
              }
            }

            console.timeEnd('Table Row Wise Computation')

            computedValuesRef.current = calculatedTableRelatedValues

            computedProcessRef.current.add(processKey)
          } catch (error) {
            console.error('Onload table computation failed', error)

            computedProcessRef.current.delete(processKey)
          }
        }

        //----- onload table computation -----//

        /* set all values to allValues store */

        const valuesBeforeInitialComputedFields = {
          ...initialAllValues,
          ...computedSumAcrossProcess,
          ...calculatedTableRelatedValues,
        }

        let computedFieldValuesOnload = {}

        const initialComputableFields = allSubfieldsResult.data.filter(
          (field) =>
            field.is_computed &&
            !field.is_subfield &&
            ONLOAD_COMPUTED_OPERATION_TYPES.has(field.metadata?.operation),
        )

        if (initialComputableFields.length > 0) {
          try {
            const ctx = {
              processPrefix: processTemplate?.data[0]?.prefix,
              processReferenceNum: processData.data[0]?.process_reference_num,
              processTemplateId: processData.data[0]?.process_template_id,
              version: processData.data[0]?.version,
            }

            const idFieldMap = {}
            allSubfieldsResult.data?.forEach((cur) => {
              idFieldMap[cur.id] = cur
            })

            computedFieldValuesOnload = await autoComputeFieldValueOptimized({
              allActivitiesCreatedAtWithVersion,
              allProcessData: nextProcessContext?.allProcessData,
              context: ctx,
              dynamicListData: dynamicListData.data,
              holidayList,
              listDataIdMetadataMap,
              selectedTableData: processData?.data[0]?.selectable_tables_data,
              tableRowIndex: null,
              tableSubfieldsMap: tableSubfieldsMap.data,
              user,
              computeFields: initialComputableFields,
              data: { allValues: valuesBeforeInitialComputedFields, rowValues: {} },
              idFieldMap,
            })
          } catch (error) {
            console.error('Onload field computation failed', error)
          }
        }

        setInitialAllValues({
          ...valuesBeforeInitialComputedFields,
          ...computedFieldValuesOnload,
        })

        if (
          allActivityData?.activityTemplate?.activity_type == ACTIVITY_TYPES.DATA_ENTRY &&
          processData?.data[0]?.version > 1
        ) {
          setReviewValues(previousProcessData?.data?.prevReviewProcessData?.review_data || {})
          setCommentValues(
            previousProcessData?.data?.prevReviewProcessData?.review_comments_data || {},
          )
        } else {
          setReviewValues(processData?.data[0]?.review_data)
          setCommentValues(processData?.data[0]?.review_comments_data)
        }
        setSelectedData(processData?.data[0]?.selectable_tables_data)

        setIsPageReady(true)
        setContentLoaded(true)
      } catch (e) {
        console.error('loading all form data', e)
      }
    })()
  }, [user])

  useEffect(() => {
    ;(async () => {
      if (!currentPageIndex && pages.length > 0 && processData?.data[0]?.id) {
        const defaultNavigatePage = await pageTemplateService.getDefaultPage(
          user,
          processData?.data[0]?.id,
        )
        const pageIndex = pages.findIndex(
          (page) => page.sequence == defaultNavigatePage?.default_page_sequence,
        )

        setCurrentPageIndex(() => pageIndex)
      }
    })()
  }, [pages])

  const handleClickOnGo = async (status) => {
    // Find missing fields
    const idFieldMap = processContext.allFields?.reduce((acc, cur) => {
      return { ...acc, [cur.id]: cur }
    }, {})

    // check if there any validation constraints present in store
    let fieldValidationErrorUpdated = {}
    let otherConstraintErrors = []

    try {
      const dateComputedFields = processContext.allFields?.filter(
        (f) => f.is_computed && f.metadata?.operation === COMPUTED_OPERATION_TYPES.DATE_COMPUTATION,
      )

      // TODO: Remove this computation call for date validations implemented  by suvamc.
      const ctx = {
        processPrefix: processContext?.processTemplate?.prefix,
        processReferenceNum: processContext?.processData?.process_reference_num,
        processTemplateId: processContext?.processData?.process_template_id,
        version: processContext?.processData?.version,
      }

      const idFieldMap = {}
      processContext.allFields?.forEach((cur) => {
        idFieldMap[cur.id] = cur
      })

      await autoComputeFieldValueOptimized({
        allActivitiesCreatedAtWithVersion: processContext?.allActivitiesCreatedAtWithVersion,
        allProcessData: processContext?.allProcessData,
        context: ctx,
        dynamicListData: null,
        holidayList: processContext?.holidayList,
        listDataIdMetadataMap: processContext.listDataIdMetadataMap,
        selectedTableData: null,
        tableRowIndex: null,
        tableSubfieldsMap: null,
        user: user,
        computeFields: dateComputedFields,
        data: { allValues, rowValues: {} },
        idFieldMap,
      })
    } catch (error) {
      toast.error(error.message, TOAST_SETTINGS_ERROR)
      setIsLoading(false)
      return
    }

    /* prevent process creation validations */
    const processCreationStatus = await processDataService.checkProcessCreationStatus(
      user,
      processContext?.processData?.process_template_id,
      processContext?.processData?.activity_template_id,
      processContext?.processData?.id,
    )

    if (processCreationStatus?.data?.messages?.length > 0) {
      processCreationStatus?.data?.messages.forEach((message) => {
        otherConstraintErrors.push(message)
      })
    }

    const routeType = allToRoutes.filter(
      (toRoute) => Number(toRoute.id) === Number(selectedToRoute),
    )[0].route_type
    /* ============= Required Field Validation ============== */

    // unique ids to prevent validation
    let requiredFieldTemplates = allSubfields.filter(
      (ft) => ft.is_required && ft.data_structure !== FIELD_TYPES.TABLE,
    )

    if (activityTemplate?.activity_type == ACTIVITY_TYPES.DATA_ENTRY && processData?.version >= 1) {
      if (routeType !== ROUTE_TYPES.REGRESS) {
        // checking based on all required fields
        const requiredValidations = requiredFieldValidation(
          user,
          allSubfields,
          requiredFieldTemplates,
          processContext.conditionalEnableDisable,
          allValues,
          selectableTableData,
        )

        /* ============= FC & TFC Validation ============== */
        //check for constraint validation
        const constraintsValidations = await finalConstraintsValidation(
          tableConstraints,
          fieldConstraints,
          allSubfields,
          listDataMetadata,
          allValues,
          selectableTableData,
          user,
          processId,
        )

        const allConstraints = [...requiredValidations, ...constraintsValidations]

        allConstraints.forEach((it) => {
          fieldValidationErrorUpdated = { ...fieldValidationErrorUpdated, ...it }
        })
      }
    }

    /* ============= Review Related Validation ============== */

    // checks for review activity
    // for all rejected value there must be comments
    // to move forward -> all values must be accepted
    if (activityTemplate?.activity_type === REVIEW_CONTEXTS.REVIEW) {
      const rejectedKeys = Object.entries(reviewValues)
        .filter(([key, value]) => !key.includes('tf_') && key !== 'updatedAt' && !value)
        .map(([key]) => key)

      const isAllTrue = rejectedKeys.length <= 0
      if (routeType === ROUTE_TYPES.PROGRESS) {
        if (!isAllTrue) {
          const fieldWhichMissingComment = rejectedKeys.map(
            (key) => idFieldMap[getFieldIdFromKey(key)?.id],
          )
          const msgExtension = fieldWhichMissingComment
            .map((it, index) => {
              if (it.is_subfield && it.table_field_template_id) {
                return `${index + 1}. ${
                  idFieldMap[it.table_field_template_id].field_template_name
                }/${it?.field_template_name}`
              } else {
                return `${index + 1}. ${it?.field_template_name}`
              }
            })
            .join('\n')

          otherConstraintErrors.push(
            <span style={{ fontSize: 12 }}>
              All data entered must be accepted to send forward.
              <br />
              <br />
              Rejected fields:
              <br />
              {msgExtension.split('\n').map((line, i) => (
                <span key={i}>
                  {line}
                  <br />
                </span>
              ))}
            </span>,
          )
        }
      } else if (routeType === ROUTE_TYPES.REGRESS) {
        if (
          isAllTrue &&
          showGoBackWarningDialog === false &&
          Object.keys(reviewValues).length > 0
        ) {
          setShowGoBackWarningDialog(true)
          return
        }

        let keysMissingComments = []
        let isAllCommentsAddedForRejects = true
        Object.keys(reviewValues)
          .filter((key) => !key.includes('tf_'))
          .forEach((key) => {
            if (
              reviewValues[key] === false &&
              commentValues[key?.replace('r_', 'c_')]?.trim() === ''
            ) {
              keysMissingComments.push(key)
              isAllCommentsAddedForRejects = false
            }
          })

        const fieldWhichMissingComment = keysMissingComments.map(
          (key) => idFieldMap[getFieldIdFromKey(key)?.id],
        )
        const msgExtension = fieldWhichMissingComment
          .map((it, index) => {
            if (it.is_subfield && it.table_field_template_id) {
              return `${index + 1}. ${idFieldMap[it.table_field_template_id].field_template_name}/${
                it?.field_template_name
              }`
            } else {
              return `${index + 1}. ${it?.field_template_name}`
            }
          })
          .join(',')

        if (!isAllCommentsAddedForRejects) {
          otherConstraintErrors.push(
            <span style={{ fontSize: 12 }}>
              All data rejected must have associated review comments.
              <br />
              <br />
              Fields missing comments:
              <br />
              {msgExtension.split(',').map((line, i) => (
                <span key={i}>
                  {line.trim()}
                  <br />
                </span>
              ))}
            </span>,
          )
        }
      }
      // else if (routeType === ROUTE_TYPES.NEUTRAL) {
      //   let isAllCommentsAddedForRejects = true
      //   Object.keys(reviewValues)
      //     .filter((key) => !key.includes('tf_'))
      //     .map((key) => {
      //       if (
      //         reviewValues[key] == false &&
      //         commentValues[key?.replace('r_', 'c_')].trim() == ''
      //       ) {
      //         isAllCommentsAddedForRejects = false
      //       }
      //     })
      //   if (!isAllCommentsAddedForRejects) {
      //     otherConstraintErrors.push('All data rejected must have associated review comments.')
      //   }
      // }
    }

    /* ============= Query Related Validation ============== */

    //check for no open queries
    const queryData = await queryService.getQuery(user, processId)
    const hasOpenQueries = queryData.queries.some((q) => q.is_open == true)
    if (hasOpenQueries) {
      otherConstraintErrors.push(
        'Cannot submit as there are open/unanswered queries. You can either wait for the query to be answered or retract the query.',
      )
    }

    // check for the currency error
    // Important: Let user complete the process in this scenario
    // until we have ways to handle the computation across currency.

    /* for (const [key, value] of Object.entries(fieldCurrencyMap)) {
      if (value === '?') {
        const field = allSubfields.find((subfield) => subfield.id.toString() === key)
        if (field && allValues['f_' + field.id] && allValues['f_' + field.id] != 0) {
          otherConstraintErrors.push(`${field.field_template_name}'s currency is invalid!`)
        }
      }
    } */

    // check if there is any validation issues in otherConstraintErrors and fieldValidationErrorUpdated
    setFieldValidationError(fieldValidationErrorUpdated)

    // ONLY ACTUAL ERRORS
    const actualErrors = Object.values(fieldValidationErrorUpdated).filter(
      (it) => it !== null && it !== undefined && it !== '',
    )

    const hasValidationErrors = actualErrors.length > 0

    if (hasValidationErrors) {
      // SHOW FEW ERRORS INDIVIDUALLY
      if (actualErrors.length < 3) {
        actualErrors.forEach((it) => {
          if (typeof it === 'string') {
            toast.error(it, TOAST_SETTINGS_ERROR)
          } else {
            toast(it, {
              ...TOAST_SETTINGS_ERROR,
              type: 'error',
            })
          }
        })
      } else {
        // MANY ERRORS
        toast.error(
          'Multiple mandatory fields lack entries. Fill in all highlighted fields to complete your submission.',
          TOAST_SETTINGS_ERROR,
        )

        console.info('Field Constraints Validation Error: ', fieldValidationErrorUpdated)
      }

      setIsLoading(false)
      return
    }

    otherConstraintErrors.forEach((it) => {
      toast.error(it, TOAST_SETTINGS_ERROR)
    })

    // BLOCK SUBMISSION FOR OTHER ERRORS
    if (otherConstraintErrors.length > 0) {
      setIsLoading(false)
      return
    }

    // at the end show preview and overall comments
    setSubmissionDialogOpen(status)
  }

  /* Task Submission */
  const handleSubmit = async (comment, routes) => {
    setIsLoading(true)

    //condition: overall must not be empty
    if (activityTemplate?.hide_overall_comment !== true) {
      if (!comment || comment.trim() === '') {
        setIsLoading(false)
        toast.error('Overall comments must be entered.', TOAST_SETTINGS_ERROR)
        return
      }
    }

    // finally submit for next activity
    const result = await processDataService.createNextProcess(user, processId, selectedToRoute, {
      overall_comment: comment.trim(),
      parallel_route_ids: routes.map((it) => it.id),
    })

    /* ==== Execute Outbound Actions ==== */
    const reservedKeywords = {
      process_reference_num: processData.process_reference_num,
      prefix: processTemplate.prefix,
      process_template_id: processTemplate.id,
      activity_template_id: activityTemplate.id,
      user_id: user.id,
      next_route_template_id: selectedToRoute,
      overall_comment: comment.trim(),
      version: processData?.version || 1,
      next_process_id: result?.data?.id_next[0] || null,
      protected_list_data_id: result?.data?.protected_list_data_id || null,
      user_token: user.token,
    }

    await executeOutboundServerAction(
      user,
      processId,
      activityTemplateId,
      selectedToRoute,
      allValues,
      allSubfields,
      listDataMetadata,
      processContext?.tableSubfieldsMap || [],
      tableSequenceAndHeaders || [],
      reservedKeywords,
      fieldCurrencyMap,
      processContext?.companyDetails?.default_currency_code,
    )

    // handle api response
    if ([201, 200].includes(result?.status)) {
      toast.success('Saved successfully.', TOAST_SETTINGS)
      deleteTaskLocally(Number(processId))
      decrementTaskBadge(NUMBER_OF_TASKS_AND_QUERIES_ANSWERED_AT_A_TIME_BY_USER)
    } else {
      setIsLoading(false)
      toast.error('Error saving data.', TOAST_SETTINGS_ERROR)
    }

    setTimeout(() => {
      markStale()
      if (isModal && onSuccess) {
        onSuccess()
      } else {
        navigate('/')
      }
    }, REDIRECT_TIMER)
  }

  // Auto scroll to top while changing page
  const autoScrollRef = useRef(null)
  useEffect(() => {
    if (currentPageIndex + 1 && autoScrollRef.current) {
      autoScrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [currentPageIndex])

  // Parse li items from html string
  const parseNotificationHtml = (html) => {
    const titleMatch = html.match(/<p>(.*?)<\/p>/)
    const liMatches = [...html.matchAll(/<li>(.*?)<\/li>/g)]
    return {
      title: titleMatch?.[1] ?? '',
      items: liMatches.map((m) => m[1]),
    }
  }

  const toggleLiExpand = (id) => {
    setExpandedNotifications((prev) => ({ ...prev, [id]: !prev[id] }))
  }
  return (
    <div
      className="dataentry-card"
      style={{ height: '85vh', overflowY: 'scroll', padding: '0px 15px 0px 0px' }}
    >
      {processData && processTemplate && (
        <>
          <Typography
            className="fontWeight800 textprimaryblue"
            variant="h4"
            ref={autoScrollRef}
            style={{
              ...headingStyles,
              marginTop: 0,
              marginBottom: 0,
              fontSize: dataEntrysubtitle,
            }}
          >
            {processTemplate?.prefix}-{processData?.process_reference_num}
          </Typography>

          <Typography
            className="textPrimary fontWeight700"
            style={{
              ...paragraphStyles,
              marginBottom: 10,
              marginTop: 0,
              fontSize: dataEntryPageTitle,
            }}
          >
            {activityTemplate?.activity_template_name} (version: {processData?.version})
          </Typography>
        </>
      )}

      {/* Notifications rendered inline, below the title */}
      {isPageReady && currentPageIndex != null && contentLoaded && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            gap: '0px',
            mb: 2,
            backgroundColor: 'white',
          }}
        >
          {notifications?.map((n) => {
            const isDuplicate = n.key === 'dcr-duplicates'
            const { title, items } = parseNotificationHtml(n.html)
            const isExpanded = expandedNotifications[n.id] ?? false
            const visibleItems = isExpanded ? items : items.slice(0, VISIBLE_LI_COUNT)
            const hiddenLiCount = items.length - VISIBLE_LI_COUNT

            return (
              <Collapse key={n.id} in={n.open} unmountOnExit>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    mb: '8px',
                    padding: '5px 14px',
                    borderRadius: '6px',
                    borderLeft: `4px solid ${isDuplicate ? '#854F0B' : '#3B6D11'}`,
                    backgroundColor: isDuplicate ? '#FAEEDA' : '#EAF3DE',
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: '100%',
                    boxSizing: 'border-box',
                    animation: isReopening ? `${fadeSlideIn} 0.4s ease-out forwards` : 'none',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '13px',
                      lineHeight: 1.5,
                      color: isDuplicate ? '#633806' : '#27500A',
                      flex: 1,
                    }}
                  >
                    {/* Title */}
                    <p style={{ margin: 0 }}>{title}</p>

                    {/* Li items */}
                    <ul style={{ margin: 0, paddingLeft: '16px' }}>
                      {visibleItems.map((item, index) => (
                        <li key={index} style={{ margin: 0 }}>
                          {item}
                        </li>
                      ))}
                    </ul>

                    {/* View more / less — only if li > 3 */}
                    {items.length > VISIBLE_LI_COUNT && (
                      <Box
                        onClick={() => toggleLiExpand(n.id)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          mt: '4px',
                          cursor: 'pointer',
                          width: 'fit-content',
                          color: isDuplicate ? '#633806' : '#27500A',
                          fontSize: '12px',
                          fontWeight: 500,
                          opacity: 0.85,
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {isExpanded ? (
                          <>
                            <ExpandLessIcon sx={{ fontSize: 14 }} />
                            View less
                          </>
                        ) : (
                          <>
                            <ExpandMoreIcon sx={{ fontSize: 14 }} />
                            {`View ${hiddenLiCount} more`}
                          </>
                        )}
                      </Box>
                    )}
                  </Box>

                  <IconButton
                    size="small"
                    onClick={() => handleCloseNotification(n.id)}
                    sx={{
                      color: isDuplicate ? '#633806' : '#27500A',
                      padding: '0px',
                      flexShrink: 0,
                      marginTop: '2px',
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
              </Collapse>
            )
          })}

          <div
            className="data-entntry-top-icon"
            style={{
              backgroundColor: '#efeaff',
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: '10px',
              boxShadow: '1px 2px 6px 0px rgb(57 85 120 / 11%)',
              border: '1px solid #ddd',
              padding: '10px 15px',
              borderRadius: '3px',
            }}
          >
            <DataEntryPageNav
              pages={pages}
              processId={processId}
              activityTemplateId={activityTemplateId}
              setCurrentPageIndex={setCurrentPageIndex}
              currentPageIndex={currentPageIndex}
              setOverallCommentsOpen={handleClickOnGo}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              processReferenceNum={processData?.process_reference_num}
              processTemplateId={processTemplate?.id}
              reviewContext={activityTemplate?.activity_type}
              onSubmit={handleSubmit}
              isPageReady={isPageReady}
              processTemplate={processTemplate}
              deadline={deadlineDate}
              onReopenNotifications={handleReopenNotifications}
              hasNotifications={notificationsRef.current.length > 0}
              closeCount={closeCount}
            />
          </div>
        </Box>
      )}
      {isPageReady && currentPageIndex != null && contentLoaded ? (
        <>
          <div className="forminput">
            <Page page={pages[currentPageIndex]} key={currentPageIndex} />
          </div>

          {ACTIVITY_TYPES.LONG_RUNNING === activityTemplate?.activity_type &&
          !user.teams.some((team) =>
            activityTemplate.lrp_reviewer_team_ids.includes(team),
          ) ? null : (
            <DataEntryFormFooter
              currentPageNum={currentPageIndex + 1}
              totalPage={pages.length}
              processActivityStatus={processData?.status}
              setSubmissionDialogOpen={handleClickOnGo}
              toRoutes={allToRoutes}
              selectedToRoute={selectedToRoute}
              setSelectedToRoute={setSelectedToRoute}
              classes={classes}
              processReferenceNum={processData?.id}
              allValues={allValues}
              fieldConstraintHiddenRoutes={fieldConstraintHiddenRoutes}
              isDeadlineExpired={isDeadlineExpired}
            />
          )}
        </>
      ) : (
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <Loader message="Setting up pages for you..." />
        </div>
      )}

      {isSubmissionDialogOpen && (
        <DataPreview
          processId={processId}
          onClose={() => setSubmissionDialogOpen(false)}
          open={!!processId}
          onSubmit={handleSubmit}
          routeTemplateId={selectedToRoute}
          isSubmission={isSubmissionDialogOpen}
          hideOverallComment={activityTemplate?.hide_overall_comment === true}
        />
      )}
      {showGoBackWarningDialog && (
        <ConfirmationBox
          title={confirmationText}
          isDialogOpen={showGoBackWarningDialog}
          setIsDialogOpen={setShowGoBackWarningDialog}
          onYes={handleClickOnGo}
        />
      )}
      <Dialog open={isLoading} width="lg">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Typography variant="h6" color="textSecondary" style={{ marginBottom: '10px' }}>
            Saving your response...
          </Typography>
          <CircularProgress color="primary" size={30} />
        </div>
      </Dialog>
    </div>
  )
}

export default DataEntryPage
